const RELAY_BASE = process.env.REACT_APP_BACKEND_URL;
const OPS_DIRECT = process.env.REACT_APP_OPS_API_URL;
const OPS_PROXY = `${process.env.REACT_APP_BACKEND_URL}/api/admin/proxy`;
const TOKEN_KEY = "jkv_admin_token";
const SECRET_KEY = "jkv_admin_secret";

export const OPS = {
  users: "admin/users",
  userStatus: (id) => `user/${id}/status`,
  convoys: "admin/convoys",
  convoyDetail: (id) => `admin/convoys/${id}`,
  clubs: "clubs",
  sos: "admin/sos",
  reports: "admin/reports",
  support: "admin/support",
  deletions: "admin/deletion-requests",
  codes: "admin/redeem-codes",
  vouchers: "admin/vouchers",
  createVoucher: "admin/vouchers",
  disableVoucher: (id) => `admin/vouchers/${id}/disable`,
  subscribers: "admin/subscribers",
  ridePasses: "admin/ride-passes",
  onlineUsers: "admin/online-users",
  grantPremium: (id) => `admin/users/${id}/grant-premium`,
  revokePremium: (id) => `admin/users/${id}/revoke-premium`,
  grantRidePass: (id) => `admin/users/${id}/grant-ride-pass`,
  revokeRidePass: (id) => `admin/users/${id}/revoke-ride-pass`,
  suspend: (id) => `admin/users/${id}/suspend`,
  unsuspend: (id) => `admin/users/${id}/unsuspend`,
  ban: (id) => `admin/users/${id}/ban`,
  unban: (id) => `admin/users/${id}/unban`,
  verifyEmail: (id) => `admin/users/${id}/verify-email`,
  disbandConvoy: (id) => `admin/convoys/${id}/disband`,
  removeClub: (id) => `admin/clubs/${id}`,
  resolveReport: (id) => `admin/reports/${id}/resolve`,
  dismissReport: (id) => `admin/reports/${id}/dismiss`,
};

export const getToken = () => localStorage.getItem(TOKEN_KEY);
export const setToken = (t) => localStorage.setItem(TOKEN_KEY, t);
export const clearToken = () => localStorage.removeItem(TOKEN_KEY);
export const getSecret = () => sessionStorage.getItem(SECRET_KEY);
export const setSecret = (s) => sessionStorage.setItem(SECRET_KEY, s);
export const clearSecret = () => sessionStorage.removeItem(SECRET_KEY);

export function formatApiError(err) {
  const detail = err?.response?.data?.detail ?? err?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return err?.message || "Request failed";
}

async function postLogin(url, email, secret) {
  const res = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email, secret }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw { detail: data.detail || "Login failed" };
  return data;
}

export async function adminLogin(email, secret) {
  let data;
  try {
    // Primary: the JomKonvoi backend's own admin auth endpoint.
    data = await postLogin(`${OPS_DIRECT}/api/admin/auth/login`, email, secret);
  } catch (err) {
    if (err?.detail) throw err; // backend answered with a real error
    // Network/CORS failure — fall back to the portal relay, which validates upstream.
    data = await postLogin(`${RELAY_BASE}/api/admin/auth/login`, email, secret);
  }
  setToken(data.token || `direct:${Date.now()}`);
  setSecret(secret);
  return data;
}

export async function checkSession() {
  const token = getToken();
  if (!token) return false;
  if (token.startsWith("direct:")) {
    // Tokenless upstream session — the caravan-gps backend authenticates each
    // request via X-Admin-Secret, so the stored secret is the session.
    return Boolean(getSecret());
  }
  try {
    const res = await fetch(`${RELAY_BASE}/api/admin/session`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) {
      clearToken();
      clearSecret();
      return false;
    }
    return true;
  } catch {
    return Boolean(getSecret());
  }
}

export async function adminLogout() {
  const token = getToken();
  if (token && !token.startsWith("direct:")) {
    try {
      await fetch(`${RELAY_BASE}/api/admin/logout`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
    } catch {}
  }
  clearToken();
  clearSecret();
}

export class OpsError extends Error {
  constructor(status, path) {
    super(
      status === 0
        ? "Could not reach the admin data service"
        : status === 404
          ? "Endpoint not available on the JomKonvoi backend yet"
          : status === 401 || status === 403
            ? "Backend rejected the admin session"
            : `Backend error (${status})`,
    );
    this.status = status;
    this.path = path;
  }
}

async function tryFetch(url, options, includeAuth) {
  const headers = { "Content-Type": "application/json" };
  const secret = getSecret();
  if (secret) headers["X-Admin-Secret"] = secret;
  if (includeAuth) {
    const token = getToken();
    if (token) headers.Authorization = `Bearer ${token}`;
  }
  return fetch(url, { ...options, headers });
}

export async function ops(path, { method = "GET", body } = {}) {
  const payload = body ? JSON.stringify(body) : undefined;
  let res;
  try {
    res = await tryFetch(`${OPS_DIRECT}/api/${path}`, { method, body: payload }, false);
  } catch {
    console.warn(`[ops] Direct call to ${OPS_DIRECT}/api/${path} failed (likely CORS) — retrying via proxy`);
    try {
      res = await tryFetch(`${OPS_PROXY}/${path}`, { method, body: payload }, true);
    } catch {
      throw new OpsError(0, path);
    }
  }
  if (!res.ok) throw new OpsError(res.status, path);
  return res.json().catch(() => null);
}

export const asList = (data, ...keys) => {
  if (Array.isArray(data)) return data;
  for (const k of keys) if (Array.isArray(data?.[k])) return data[k];
  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.events)) return data.events;
  return [];
};

export const fmtDate = (v) => {
  if (!v) return "—";
  const d = typeof v === "number" ? new Date(v) : new Date(v);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString();
};
